import ConfirmationDialog from '@/components/@material-extend/ConfirmationDialog';
import { MoreMenuButton } from '@/components/MoreMenuButton';
import { PATH_ADMIN } from '@/routes/paths.js';
import useStore from '@/store/use-store';
import arrowheadDownOutline from '@iconify/icons-eva/arrowhead-down-outline';
import arrowheadUpOutline from '@iconify/icons-eva/arrowhead-up-outline';
import checkmarkCircle2Outline from '@iconify/icons-eva/checkmark-circle-2-outline';
import editFill from '@iconify/icons-eva/edit-fill';
import emailOutline from '@iconify/icons-eva/email-outline';
import peopleOutline from '@iconify/icons-eva/people-outline';
import trash2Outline from '@iconify/icons-eva/trash-2-outline';
import { Icon } from '@iconify/react';
import {
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import PropTypes from 'prop-types';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, generatePath } from 'react-router-dom';
import { toast } from 'react-toastify';

UserMoreMenu.propTypes = {
  id: PropTypes.number,
  email: PropTypes.string,
  isAdmin: PropTypes.bool,
  isActive: PropTypes.bool
};

export default function UserMoreMenu({ id, email, isAdmin, isActive }) {
  const ref = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [impact, setImpact] = useState(null);
  const {
    deleteUser,
    getUserDeletionImpact,
    passwordReset,
    impersonateUser,
    promoteAdminUser,
    demoteAdminUser,
    approveUser
  } = useStore((state) => state);
  const { t } = useTranslation();

  const onApproveClick = () => {
    approveUser(id)
      .then(() => toast.success(t('user.approve.success')))
      .catch(() => toast.error(t('user.approve.error')));
    setIsOpen(false);
  };

  const onDeleteClick = () => {
    setIsOpen(false);
    getUserDeletionImpact(id)
      .then((data) => {
        setImpact(data);
        setConfirmOpen(true);
      })
      .catch(() => toast.error(t('user.delete.impact_error')));
  };

  const onConfirmDelete = () => {
    deleteUser(id)
      .then(() => toast.success(t('user.delete.success')))
      .catch((err) => {
        if (err?.response?.data?.detail === 'self-delete') {
          toast.error(t('user.delete.self_error'));
        } else {
          toast.error(t('user.delete.error'));
        }
      })
      .finally(() => {
        setConfirmOpen(false);
        setImpact(null);
      });
  };

  const onImpersonateClick = () => {
    impersonateUser(email);
  };

  const onResetPasswordClick = () => {
    passwordReset(email);
    setIsOpen(false);
  };

  const onPromoteClick = () => {
    promoteAdminUser(id).then(() => toast.success('Gebruiker gepromoveerd'));
    setIsOpen(false);
  };

  const onDemoteClick = () => {
    demoteAdminUser(id).then(() => toast.success('Gebruiker gedemoveerd'));
    setIsOpen(false);
  };

  return (
    <>
      <MoreMenuButton isOpen={isOpen} setIsOpen={setIsOpen} menuId="user-more-menu" ref={ref} />

      <Menu
        id="user-more-menu"
        open={isOpen}
        anchorEl={ref.current}
        onClose={() => setIsOpen(false)}
        PaperProps={{
          sx: { width: 200, maxWidth: '100%' }
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          component={RouterLink}
          to={generatePath(PATH_ADMIN.user, { id })}
          sx={{ color: 'text.secondary' }}
        >
          <ListItemIcon>
            <Icon icon={editFill} width={24} height={24} />
          </ListItemIcon>
          <ListItemText primary={t('edit')} primaryTypographyProps={{ variant: 'body2' }} />
        </MenuItem>

        <MenuItem sx={{ color: 'text.secondary' }} onClick={() => onResetPasswordClick()}>
          <ListItemIcon>
            <Icon icon={emailOutline} width={24} height={24} />
          </ListItemIcon>
          <ListItemText
            primary={t('user.mail_password')}
            primaryTypographyProps={{ variant: 'body2' }}
          />
        </MenuItem>

        {!isActive && (
          <MenuItem sx={{ color: 'success.main' }} onClick={() => onApproveClick()}>
            <ListItemIcon sx={{ color: 'success.main' }}>
              <Icon icon={checkmarkCircle2Outline} width={24} height={24} />
            </ListItemIcon>
            <ListItemText
              primary={t('user.approve.action')}
              primaryTypographyProps={{ variant: 'body2' }}
            />
          </MenuItem>
        )}

        <MenuItem sx={{ color: 'text.secondary' }} onClick={() => onDeleteClick()}>
          <ListItemIcon>
            <Icon icon={trash2Outline} width={24} height={24} />
          </ListItemIcon>
          <ListItemText primary={t('delete')} primaryTypographyProps={{ variant: 'body2' }} />
        </MenuItem>

        <MenuItem
          sx={{ color: 'text.secondary' }}
          onClick={() => (isAdmin ? onDemoteClick() : onPromoteClick())}
        >
          <ListItemIcon>
            <Icon
              icon={isAdmin ? arrowheadDownOutline : arrowheadUpOutline}
              width={24}
              height={24}
            />
          </ListItemIcon>
          <ListItemText
            primary={isAdmin ? t('demote') : t('promote')}
            primaryTypographyProps={{ variant: 'body2' }}
          />
        </MenuItem>

        <MenuItem sx={{ color: 'text.secondary' }} onClick={() => onImpersonateClick()}>
          <ListItemIcon>
            <Icon icon={peopleOutline} width={24} height={24} />
          </ListItemIcon>
          <ListItemText primary={t('impersonate')} primaryTypographyProps={{ variant: 'body2' }} />
        </MenuItem>
      </Menu>

      {impact && (
        <ConfirmationDialog
          open={confirmOpen}
          options={{
            title: t('user.delete.title'),
            allowClose: true,
            buttonOrder: ['cancel', 'confirm'],
            cancellationText: t('cancel'),
            confirmationText: t('delete'),
            confirmationButtonProps: { color: 'error', variant: 'contained' },
            content: (
              <>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {t('user.delete.warning')}
                </Typography>
                <Typography variant="subtitle2">
                  {`${impact.user.firstName ?? ''} ${impact.user.lastName ?? ''}`.trim()}
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                  {impact.user.email}
                </Typography>

                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {t('user.delete.ships_with_certificates', {
                    ships: impact.totals.ships,
                    certificates: impact.totals.issuedCertificates
                  })}
                </Typography>

                {impact.ships.length > 0 && (
                  <TableContainer sx={{ mb: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('user.delete.ship_name')}</TableCell>
                          <TableCell align="right">{t('user.delete.certificates')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {impact.ships.map((ship, index) => (
                          <TableRow key={ship.eni ?? index}>
                            <TableCell>{ship.title || ship.eni || '—'}</TableCell>
                            <TableCell align="right">{ship.issuedCertificates}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            )
          }}
          onCancel={() => setConfirmOpen(false)}
          onClose={() => setConfirmOpen(false)}
          onConfirm={onConfirmDelete}
        />
      )}
    </>
  );
}
